import React, { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import {
  Printer,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import {
  FETCH_CHUNK,
  REPORT_TABLE_HEADER_BG,
  REPORT_TABLE_HEADER_TEXT,
  REPORT_TABLE_HEADER_BORDER,
  applyExcelHeaderRowStyle,
  printHtmlInSameTab,
} from './reportPrintHelpers';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function defaultFilters() {
  return {
    dateFrom: todayInput(),
    dateTo: todayInput(),
    employeeIds: [],
    employeeLabels: {},
  };
}

function normalizeIdArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
}

function buildPayload(skip, take, f) {
  const d0 = new Date(f.dateFrom + 'T12:00:00');
  const d1 = new Date(f.dateTo + 'T12:00:00');
  return {
    Skip: skip,
    Take: take,
    FromYear: d0.getFullYear(),
    FromMonth: d0.getMonth() + 1,
    FromDay: d0.getDate(),
    ToYear: d1.getFullYear(),
    ToMonth: d1.getMonth() + 1,
    ToDay: d1.getDate(),
    EmployeeIds: normalizeIdArray(f.employeeIds),
    ClientId: 0,
    IsPickupDate: false,
    IsCreatedDate: false,
    StatusIds: [],
    VoucherNo: '',
    BillNo: '',
    Keyword: '',
    AreaIds: [],
    IsPaid: false,
    IsNotPaid: false,
    IsByDelPickup: false,
  };
}

function formatDate(d) {
  if (d == null || d === '') return '—';
  if (typeof d === 'string') {
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
    const dt = new Date(d.Year, d.Month - 1, d.Day);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function num(n) {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  return Number.isNaN(v) ? '—' : v.toFixed(2);
}

function parseNumberField(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function sumAmount(rows) {
  let s = 0;
  for (const r of rows) {
    s += parseNumberField(r.Amount ?? r.amount);
  }
  return s;
}

function rowToFlat(r, i) {
  const emp = r.Employee ?? r.employee;
  const exp = r.Expense ?? r.expense;
  return {
    '#': i + 1,
    Date: formatDate(r.Date ?? r.date),
    Amount: r.Amount ?? r.amount ?? '',
    BillNo: r.BillNo ?? r.billNo ?? '',
    Expense: exp?.Name ?? exp?.name ?? '',
    Employee: emp?.Name ?? emp?.name ?? '',
    EmployeeMobile: emp?.MobileNo1 ?? emp?.mobileNo1 ?? '',
  };
}

function rowMatchesTableSearch(r, q) {
  if (!q) return true;
  const o = rowToFlat(r, 0);
  const { '#': _n, ...rest } = o;
  const hay = Object.values(rest)
    .map((x) => String(x ?? '').toLowerCase())
    .join(' ');
  return hay.includes(q);
}

/** Group rows by driver (employee) name. */
function groupRowsByEmployee(rows) {
  const map = new Map();
  for (const r of rows) {
    const emp = r.Employee ?? r.employee;
    const nameRaw = String(emp?.Name ?? emp?.name ?? '').trim();
    const id = emp?.Id ?? emp?.id;
    const idNum = id != null && id !== '' ? Number(id) : NaN;
    const hasId = !Number.isNaN(idNum) && idNum !== 0;

    let key;
    let label;
    if (nameRaw) {
      key = `name:${nameRaw.toLowerCase()}`;
      label = nameRaw;
    } else if (hasId) {
      key = `id:${idNum}`;
      label = `Driver #${idNum}`;
    } else {
      key = '_unknown';
      label = 'Unknown';
    }

    if (!map.has(key)) {
      map.set(key, { key, id: hasId ? idNum : null, label, rows: [] });
    }
    map.get(key).rows.push(r);
  }
  const groups = [...map.values()];
  groups.sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base', numeric: true })
  );
  return groups;
}

const PRINT_COLUMN_DEFS = [
  { key: '#', label: '#' },
  { key: 'Date', label: 'Date' },
  { key: 'Amount', label: 'Amount' },
  { key: 'BillNo', label: 'Bill no' },
  { key: 'Expense', label: 'Expense' },
  { key: 'Employee', label: 'Driver' },
  { key: 'EmployeeMobile', label: 'Driver mob' },
];

const COL_COUNT = PRINT_COLUMN_DEFS.length;

const RIGHT_ALIGN_KEYS = new Set(['Amount']);

const EXTRA_RIGHT_ALIGN = new Set([]);

const GRID_COLUMNS = PRINT_COLUMN_DEFS.filter((c) => c.key !== '#').map((c) => ({
  field: c.key,
  title: c.label,
  hideable: true,
  align: RIGHT_ALIGN_KEYS.has(c.key) || EXTRA_RIGHT_ALIGN.has(c.key) ? 'right' : 'left',
}));

function gridCellValue(row, field) {
  const o = rowToFlat(row, 0);
  const raw = o[field];
  if (RIGHT_ALIGN_KEYS.has(field)) return num(raw);
  if (raw == null || raw === '') return '—';
  return String(raw);
}


const AMOUNT_COL_INDEX = PRINT_COLUMN_DEFS.findIndex((c) => c.key === 'Amount');

function defaultPrintColumnSelection() {
  return Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, true]));
}

function filterSummary(f) {
  const parts = [`${f.dateFrom} → ${f.dateTo}`, 'expense date'];
  if (f.employeeIds?.length) {
    const labels = f.employeeLabels || {};
    const preview = f.employeeIds
      .slice(0, 2)
      .map((id) => labels[String(id)] || `#${id}`)
      .join(', ');
    parts.push(
      `${f.employeeIds.length} driver(s)${preview ? ` (${preview}${f.employeeIds.length > 2 ? '…' : ''})` : ''}`
    );
  } else parts.push('all drivers');
  return parts.join(' · ');
}

function tdClass(key) {
  if (key === '#') return 'px-2 py-2 tabular-nums text-gray-600';
  if (RIGHT_ALIGN_KEYS.has(key)) return 'px-2 py-2 text-right tabular-nums';
  return 'whitespace-nowrap px-2 py-2';
}

function formatEmployeeListItem(e) {
  const name = e.Name ?? e.name ?? '';
  const mobile = e.MobileNo1 ?? e.mobileNo1 ?? '';
  const parts = [name, mobile].filter(Boolean);
  return parts.join(' · ') || `Employee ${e.Id ?? e.id}`;
}

function AmountSubtotalRow({ total, label, variant }) {
  const tail = COL_COUNT - AMOUNT_COL_INDEX - 1;
  const rowClass =
    variant === 'grand'
      ? 'border-t-2 border-amber-400 bg-amber-100/95 font-semibold text-amber-950'
      : 'border-b border-amber-200/80 bg-amber-50/95 font-semibold text-amber-950';
  return (
    <tr className={rowClass}>
      <td
        colSpan={AMOUNT_COL_INDEX}
        className="px-2 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-amber-900"
      >
        {label}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-xs">{num(total)}</td>
      <td colSpan={tail} className="px-2 py-1.5 text-xs text-amber-800/50">
        {'\u00a0'}
      </td>
    </tr>
  );
}

export default function DriverExpenseReport() {
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const [draft, setDraft] = useState(() => defaultFilters());
  const [filterModalOpen, setFilterModalOpen] = useState(true);

  const [employeePickerSearch, setEmployeePickerSearch] = useState('');
  const [employeePickerResults, setEmployeePickerResults] = useState([]);
  const [employeePickerLoading, setEmployeePickerLoading] = useState(false);
  const employeeSearchSeq = useRef(0);
  const [collapsedTableGroupKeys, setCollapsedTableGroupKeys] = useState(() => new Set());

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [printPreparing, setPrintPreparing] = useState(false);
  const [printColumnModalOpen, setPrintColumnModalOpen] = useState(false);
  const [printColumnSelection, setPrintColumnSelection] = useState(defaultPrintColumnSelection);
  const [error, setError] = useState('');

  useEffect(() => {
    if (filterModalOpen) {
      setDraft({
        ...appliedFilters,
        employeeIds: appliedFilters.employeeIds || [],
        employeeLabels: appliedFilters.employeeLabels || {},
      });
      setEmployeePickerSearch('');
      setEmployeePickerResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModalOpen]);

  useEffect(() => {
    if (!filterModalOpen) return;
    const q = employeePickerSearch.trim();
    if (q.length < 2) {
      setEmployeePickerResults([]);
      setEmployeePickerLoading(false);
      return;
    }
    const seq = ++employeeSearchSeq.current;
    const timer = setTimeout(async () => {
      setEmployeePickerLoading(true);
      try {
        const res = await MainService.DriversInGrid({
          filter: {
            filters: [{ field: 'Name', operator: 'contains', value: q }],
            logic: 'and',
          },
        });
        if (seq !== employeeSearchSeq.current) return;
        const raw = res?.Data ?? res?.data ?? res;
        setEmployeePickerResults(Array.isArray(raw) ? raw : []);
      } catch (e) {
        console.error(e);
        if (seq === employeeSearchSeq.current) setEmployeePickerResults([]);
      } finally {
        if (seq === employeeSearchSeq.current) setEmployeePickerLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [employeePickerSearch, filterModalOpen]);

  const loadReport = async (f = appliedFilters) => {
    setCollapsedTableGroupKeys(new Set());
    setLoading(true);
    setHasLoaded(false);
    setLoadProgress({ loaded: 0, total: 0 });
    setRows([]);
    setTotal(0);
    setError('');
    try {
      let skip = 0;
      let serverTotal = 0;
      const accumulated = [];
      for (;;) {
        const body = buildPayload(skip, FETCH_CHUNK, f);
        const res = await MainService.DriverExpenseReportV2(body);
        const chunk = res?.Data ?? res?.data ?? [];
        const arr = Array.isArray(chunk) ? chunk : [];
        serverTotal = Number(res?.Total ?? res?.total ?? 0) || 0;
        accumulated.push(...arr);
        setRows([...accumulated]);
        setTotal(serverTotal);
        setLoadProgress({ loaded: accumulated.length, total: serverTotal });
        if (arr.length === 0) break;
        skip += arr.length;
        if (skip >= serverTotal) break;
        if (arr.length < FETCH_CHUNK) break;
      }
      setHasLoaded(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load driver expense report.');
      setRows([]);
      setTotal(0);
      setHasLoaded(false);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  };

  const applyModalFilters = () => {
    setAppliedFilters({ ...draft });
    setFilterModalOpen(false);
    setTableSearch('');
    loadReport({ ...draft });
  };

  const cancelModal = () => {
    setDraft({ ...appliedFilters });
    setFilterModalOpen(false);
  };

  const openFilterModal = () => setFilterModalOpen(true);

  const onToolbarRefresh = () => {
    if (!hasLoaded || loading) return;
    setTableSearch('');
    loadReport(appliedFilters);
  };

  const clearModalFilters = () => {
    setDraft(defaultFilters());
  };

  const searchQ = tableSearch.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchQ) return rows;
    return rows.filter((r) => rowMatchesTableSearch(r, searchQ));
  }, [rows, searchQ]);

  const rowsGroupedByEmployee = useMemo(() => groupRowsByEmployee(filteredRows), [filteredRows]);
  const columns = useMemo(() => GRID_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: filteredRows, getCellValue: gridCellValue });
  const grandAmountSum = useMemo(() => sumAmount(grid.processedRows), [grid.processedRows]);

  const toggleTableGroupCollapsed = (groupKey) => {
    setCollapsedTableGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const onExportExcel = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then export.');
      return;
    }
    setExcelExporting(true);
    setError('');
    try {
      if (!filteredRows.length) {
        setError('No rows to export.');
        return;
      }
      let n = 0;
      const sheetData = rowsGroupedByEmployee.flatMap((g) =>
        g.rows.map((r) => {
          n += 1;
          const flat = rowToFlat(r, n - 1);
          return { DriverGroup: g.label, ...flat };
        })
      );
      const ws = XLSX.utils.json_to_sheet(sheetData);
      applyExcelHeaderRowStyle(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'DriverExpenses');
      const fname = `driver-expense-report-${appliedFilters.dateFrom}-${appliedFilters.dateTo}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Excel export failed.');
    } finally {
      setExcelExporting(false);
    }
  };

  const openPrintColumnModal = () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then print.');
      return;
    }
    if (!filteredRows.length) {
      setError('No rows to print.');
      return;
    }
    setError('');
    setPrintColumnSelection(defaultPrintColumnSelection());
    setPrintColumnModalOpen(true);
  };

  const executeDriverExpensePrint = async () => {
    const activeCols = PRINT_COLUMN_DEFS.filter((d) => printColumnSelection[d.key]);
    if (!activeCols.length) {
      setError('Select at least one column to print.');
      return;
    }

    setPrintColumnModalOpen(false);
    setPrintPreparing(true);
    setError('');
    try {
      if (!filteredRows.length) {
        setError('No rows to print.');
        return;
      }

      const groupsAtPrint = groupRowsByEmployee(filteredRows);
      const esc = (s) =>
        String(s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (e) {
        console.warn('Driver expense report print: no print header', e);
      }

      const headHtml = `<tr>${activeCols.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr>`;
      const colSpan = activeCols.length;
      let printRowNum = 0;
      const bodyHtml = groupsAtPrint
        .map((g) => {
          const banner = `<tr class="group-banner"><td colspan="${colSpan}">${esc(g.label)} — ${g.rows.length} line(s) · ${esc(num(sumAmount(g.rows)))}</td></tr>`;
          const dataRows = g.rows
            .map((r) => {
              printRowNum += 1;
              const o = rowToFlat(r, printRowNum - 1);
              const cells = activeCols.map((c) => o[c.key]);
              return `<tr>${cells.map((cell) => `<td>${esc(cell)}</td>`).join('')}</tr>`;
            })
            .join('');
          return banner + dataRows;
        })
        .join('');

      const summary = esc(filterSummary(appliedFilters));
      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header print-repeat-header-cell">${printHeaderHtml}</div>`
        : '';
      const thBg = REPORT_TABLE_HEADER_BG;
      const thColor = REPORT_TABLE_HEADER_TEXT;
      const thBorder = REPORT_TABLE_HEADER_BORDER;
      const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>&#x200b;</title>
        <style>
          html,body{margin:0;padding:0}
          body{font-family:system-ui,sans-serif;font-size:10px;color:#111}
          .report-print-inner{box-sizing:border-box;padding:12px}
          .company-print-header{margin-bottom:14px}
          .company-print-header img{max-height:72px}
          .report-title{font-size:13px;font-weight:700;margin:0 0 8px;color:#111}
          .meta{color:#444;margin-bottom:12px;max-width:100%}
          table{border-collapse:collapse;width:100%;table-layout:auto}
          th,td{border:1px solid ${thBorder};padding:3px 4px;word-wrap:break-word;vertical-align:top}
          th{background:${thBg} !important;color:${thColor};font-weight:700;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          td{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          tr.group-banner td{background:#ecfdf5 !important;font-weight:700;font-size:10px;padding:5px 6px}
          @media print{
            @page{size:auto;margin:0}
            html,body{margin:0 !important;padding:0 !important}
            .report-print-inner{padding:14mm 16mm 16mm 16mm !important;box-sizing:border-box;-webkit-box-decoration-break:clone;box-decoration-break:clone}
            thead{display:table-header-group}
            .company-print-header{page-break-inside:avoid}
          }
        </style></head><body>
        <div class="report-print-inner">
        ${headerBlock}
        <p class="report-title">Driver Expense Report</p>
        <div class="meta">${summary}</div>
        <p style="margin:0 0 8px;font-size:10px;color:#666">${filteredRows.length} line(s) · ${esc(num(grandAmountSum))} total · ${groupsAtPrint.length} driver group(s)</p>
        <table><thead>${headHtml}</thead><tbody>${bodyHtml}</tbody></table>
        </div>
        </body></html>`;
      await printHtmlInSameTab(htmlDoc, 'Driver expense report print');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Print failed.');
    } finally {
      setPrintPreparing(false);
    }
  };

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const toggleDraftEmployee = (c) => {
    const id = Number(c.Id ?? c.id);
    if (Number.isNaN(id)) return;
    const label = formatEmployeeListItem(c);
    setDraft((d) => {
      const next = new Set((d.employeeIds || []).map(Number));
      const labels = { ...(d.employeeLabels || {}) };
      if (next.has(id)) {
        next.delete(id);
        delete labels[String(id)];
      } else {
        next.add(id);
        labels[String(id)] = label;
      }
      return { ...d, employeeIds: [...next], employeeLabels: labels };
    });
  };

  const removeDraftEmployeeId = (id) => {
    setDraft((d) => {
      const labels = { ...(d.employeeLabels || {}) };
      delete labels[String(id)];
      return { ...d, employeeIds: (d.employeeIds || []).filter((x) => Number(x) !== Number(id)), employeeLabels: labels };
    });
  };

  return (
    <div className="space-y-4 relative">
      {printColumnModalOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="driver-expense-print-columns-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <h3 id="driver-expense-print-columns-title" className="text-lg font-semibold text-gray-900">
                Columns for print / PDF
              </h3>
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-5 pt-3 text-sm text-gray-600">
              Choose which columns appear in the printable document. Company print header is added at the top automatically.
            </p>
            <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-gray-100">
              <button
                type="button"
                className="text-xs font-medium text-emerald-700 hover:underline"
                onClick={() => setPrintColumnSelection(defaultPrintColumnSelection())}
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() =>
                  setPrintColumnSelection(Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, false])))
                }
                className="text-xs font-medium text-gray-600 hover:underline"
              >
                Clear all
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-2">
              {PRINT_COLUMN_DEFS.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!printColumnSelection[col.key]}
                    onChange={() =>
                      setPrintColumnSelection((prev) => ({
                        ...prev,
                        [col.key]: !prev[col.key],
                      }))
                    }
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-800">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void executeDriverExpensePrint()}
                disabled={!PRINT_COLUMN_DEFS.some((d) => printColumnSelection[d.key])}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="driver-expense-filter-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h3 id="driver-expense-filter-title" className="text-lg font-semibold text-gray-900">
                Driver expense report filters
              </h3>
              <button
                type="button"
                onClick={cancelModal}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">From date</span>
                  <input
                    type="date"
                    value={draft.dateFrom}
                    onChange={(e) => updateDraft({ dateFrom: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">To date</span>
                  <input
                    type="date"
                    value={draft.dateTo}
                    onChange={(e) => updateDraft({ dateTo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
              </div>

              <div className="rounded-lg border border-gray-100 p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-gray-700">Drivers (optional)</label>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:underline"
                    onClick={() => updateDraft({ employeeIds: [], employeeLabels: {} })}
                  >
                    Clear all
                  </button>
                </div>
                <div>
                  <label htmlFor="driver-expense-driver-search" className="text-xs text-gray-600">
                    Search drivers (min. 2 characters)
                  </label>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      id="driver-expense-driver-search"
                      type="search"
                      value={employeePickerSearch}
                      onChange={(e) => setEmployeePickerSearch(e.target.value)}
                      placeholder="Name or mobile…"
                      autoComplete="off"
                      className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white">
                  {employeePickerLoading && (
                    <p className="px-3 py-6 text-center text-sm text-gray-500">Searching…</p>
                  )}
                  {!employeePickerLoading && employeePickerSearch.trim().length < 2 && (
                    <p className="px-3 py-6 text-center text-sm text-gray-500">Type at least 2 characters to search.</p>
                  )}
                  {!employeePickerLoading &&
                    employeePickerSearch.trim().length >= 2 &&
                    employeePickerResults.length === 0 && (
                      <p className="px-3 py-6 text-center text-sm text-gray-500">No drivers match this search.</p>
                    )}
                  {!employeePickerLoading &&
                    employeePickerResults.map((c) => {
                      const id = Number(c.Id ?? c.id);
                      const checked = draft.employeeIds.map(Number).includes(id);
                      return (
                        <label
                          key={`d-${id}`}
                          className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDraftEmployee(c)}
                            className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-900 leading-snug">{formatEmployeeListItem(c)}</span>
                        </label>
                      );
                    })}
                </div>
                {draft.employeeIds.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-700">Selected ({draft.employeeIds.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {draft.employeeIds.map((id) => {
                        const sid = String(id);
                        const lab = (draft.employeeLabels && draft.employeeLabels[sid]) || `Driver #${id}`;
                        return (
                          <span
                            key={sid}
                            className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 pl-2.5 pr-1 py-0.5 text-xs text-emerald-900"
                          >
                            <span className="truncate" title={lab}>
                              {lab}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeDraftEmployeeId(id)}
                              className="shrink-0 rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200/80"
                              aria-label={`Remove ${lab}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={clearModalFilters}
                className="px-4 py-2 rounded-lg border border-amber-200 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100"
              >
                Clear all filters
              </button>
              <button
                type="button"
                onClick={cancelModal}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyModalFilters}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                Apply filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="page-title text-2xl font-semibold text-gray-900">Driver Expense Report</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={onToolbarRefresh}
            disabled={loading || !hasLoaded}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={excelExporting || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {excelExporting ? 'Exporting…' : 'Excel'}
          </button>
          <button
            type="button"
            onClick={openPrintColumnModal}
            disabled={printPreparing || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
            title="Choose columns, then print or Save as PDF"
          >
            <Printer className="w-4 h-4" />
            {printPreparing ? 'Preparing…' : 'Print / PDF'}
          </button>
        </div>
      </div>

      {hasLoaded && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Active filters: {filterSummary(appliedFilters)}
        </p>
      )}

      {(hasLoaded || loading) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="relative max-w-xl flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              disabled={loading && rows.length === 0}
              placeholder="Search loaded rows…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-400"
              aria-label="Search in table"
            />
          </div>
          {rowsGroupedByEmployee.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500">Driver groups:</span>
              <button
                type="button"
                onClick={() => setCollapsedTableGroupKeys(new Set())}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-800 hover:bg-gray-50"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={() => setCollapsedTableGroupKeys(new Set(rowsGroupedByEmployee.map((g) => g.key)))}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-800 hover:bg-gray-50"
              >
                Collapse all
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm">{error}</div>
      )}

      {!hasLoaded && !loading && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-gray-600 text-sm">
          Use <strong>Apply filters</strong> in the dialog to load the report (dialog opens automatically when you open this
          page).
        </div>
      )}

      <div className="space-y-2">
        {loading && loadProgress != null && (
          <div className="rounded-lg border border-gray-200 bg-white px-3 pb-2 pt-3 shadow-sm">
            <div className="mb-1.5 flex justify-between text-xs text-gray-600">
              <span className="font-medium text-gray-800">Loading…</span>
              <span className="tabular-nums text-gray-700">
                {loadProgress.total > 0
                  ? `${loadProgress.loaded} / ${loadProgress.total} rows`
                  : rows.length > 0
                    ? `${rows.length} rows…`
                    : 'Connecting…'}
              </span>
            </div>
            {loadProgress.total > 0 ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.min(100, (loadProgress.loaded / Math.max(loadProgress.total, 1)) * 100)}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-emerald-400" />
              </div>
            )}
          </div>
        )}
        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading && rows.length === 0}
          emptyText={rows.length === 0 ? 'No rows for this filter.' : 'No rows match your search.'}
          minWidth="980px"
          compact
          tableClassName="w-full text-left text-xs"
          theadClassName="sticky top-0 z-10 border-b border-emerald-300 bg-emerald-100"
          maxHeight="75vh"
          footerExtra={hasLoaded && !loading && grid.processedRows.length > 0 ? (
            <AmountSubtotalRow
              total={grandAmountSum}
              label={`Grand total · ${grid.processedRows.length} line${grid.processedRows.length === 1 ? '' : 's'}`}
              variant="grand"
            />
          ) : null}
          renderCell={(row, column) => row[column.field]}
        />
      </div>
    </div>
  );
}
